/**
 * The sends SMSWiser refused today, per gateway (19/10/2026), as one
 * section that opens like the others: closed, which gateways and the last
 * refusal; open, one row per refused drop — the gateway, the container, the
 * drop (list, time), what SMSWiser said, the attempt, whether Slack heard
 * it — newest first. Open by default when there is any; the Errors tab has
 * the rest.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import type { DropGenErrorRow } from '@trafficpulse/shared';

import { instantDk, instantText } from '../../../utils/clock/dk-label';
import { ContainerChip } from '../../shared/ContainerChip';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { Collapse } from '../../timeline/section/Collapse';
import { SectionAction } from '../../timeline/section/SectionAction';
import { SectionToggle } from '../../timeline/section/SectionToggle';
import type { GatewayToday } from '../gateways-today';

const GRID = 'grid grid-cols-[minmax(160px,1.1fr)_minmax(140px,1fr)_minmax(200px,1.3fr)_minmax(240px,2fr)_64px_92px] items-center gap-3 px-5';

const shortName = (name: string) => name.replace(/\s*\([^)]*\)\s*$/, '');

const summary = (gateways: GatewayToday[], clock: Parameters<typeof instantText>[1]) => {
  const last = gateways.flatMap((g) => g.failures).sort((a, b) => (a.at < b.at ? 1 : -1))[0];
  return [gateways.map((g) => `${shortName(g.name)} ×${g.failures.length}`).join(' · '), last ? `last ${instantText(last.at, clock)}` : null].filter(Boolean).join(' · ');
};

const Row: FC<{ gateway: GatewayToday; row: DropGenErrorRow; colorOf: (containerId: string) => string }> = ({ gateway, row, colorOf }) => {
  const clock = useCountryClock();
  return (
    <div className={`${GRID} border-t border-border/30 py-1.5 text-xs`} data-testid="send-failure-row">
      <span className="truncate font-medium" title={gateway.name}>{shortName(gateway.name)}</span>
      <span>{row.containerName ? <ContainerChip name={row.containerName} color={row.containerId ? colorOf(row.containerId) : '#64748b'} className="!px-2 !text-[10.5px]" /> : <span className="text-muted-foreground">—</span>}</span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-mono" title={row.listName ?? undefined}>{row.listName ?? 'list'}</span>
        <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground">{row.dropAt ? instantText(row.dropAt, clock) : instantText(row.at, clock)}</span>
      </span>
      <span className="truncate text-muted-foreground" title={row.message}>{row.message}</span>
      <span className="text-right font-mono tabular-nums">{row.attempt ?? '—'}</span>
      <span className="text-right font-mono text-[11px] tabular-nums">{row.notifiedAt ? <span className="text-success">✓ {instantDk(row.notifiedAt).label}</span> : row.notifyError ? <span className="text-destructive">✗</span> : <span className="text-muted-foreground">queued</span>}</span>
    </div>
  );
};

export const SendFailuresSection: FC<{ gateways: GatewayToday[]; colorOf: (containerId: string) => string; onOpenErrors?: () => void }> = ({ gateways, colorOf, onOpenErrors }) => {
  const [open, setOpen] = useState(true);
  const clock = useCountryClock();
  if (gateways.length === 0) return null;
  const count = gateways.reduce((n, g) => n + g.failures.length, 0);
  return (
    <div className="border-t border-border/40" data-testid="send-failures-section">
      <SectionToggle
        icon={ShieldAlert}
        title="Send failures today"
        badge={count}
        summary={summary(gateways, clock)}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        actions={onOpenErrors ? <SectionAction icon={ShieldAlert} onClick={onOpenErrors}>Errors tab</SectionAction> : null}
      />
      <Collapse open={open}>
        <div className="overflow-x-auto pb-2">
          <div className="min-w-[900px]">
            <div className={`${GRID} py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
              <span>Gateway</span><span>Container</span><span>Drop</span><span>SMSWiser said</span><span className="text-right">Attempt</span><span className="text-right">Slack</span>
            </div>
            {gateways.flatMap((g) => g.failures.map((row) => <Row key={row.id} gateway={g} row={row} colorOf={colorOf} />))}
          </div>
        </div>
        <p className="px-5 pb-3 text-[11px] text-muted-foreground">A refused drop stays a draft and auto-send tries it again each minute; refusals in a row halt auto-send until a person presses Resume. A drop a later attempt sends leaves this list.</p>
      </Collapse>
    </div>
  );
};
